/**
 * "Has this value stopped changing?" — for defects that are only real once the teacher stops typing.
 *
 * An equation is invalid at almost every keystroke on the way to being valid: `{n}`, `{n}*`, `{n}*2`
 * passes through one unparseable state per character. Reporting that is not diagnosis, it is
 * narration, and it makes the panel flicker red while someone is mid-word. The same is true of an
 * answer option in equation mode, and of everything DERIVED from a half-typed equation — a coverage
 * gap computed from `{n}*` is a fact about a string the teacher never meant to author.
 *
 * Why a settle timer rather than a focus/blur gate. The first attempt at this gated on focus, and it
 * was wrong in a way worth recording: a block computes its issues TWICE — once from `block.data` for
 * the card and the test panel, once from the editor's live state for the editor panel — and gating
 * one list left the two surfaces contradicting each other, the test panel showing an error the
 * editor denied. A settle flag is a single piece of state both lists can read, so they cannot
 * disagree by construction. It also sidesteps blur entirely, which is unreliable here: the equation
 * field is a contenteditable whose variable picker steals focus, so "blurred" fires when the teacher
 * is plainly still working on it.
 *
 * Deliberately NOT a debounce of the value itself. The equation must stay live everywhere — the
 * card, the preview and the evaluator all keep updating per keystroke, exactly as before. The only
 * thing this delays is the decision to CALL something broken.
 */
import { useEffect, useRef, useState } from 'react';

import type { BlockIssue } from './block-issues';

/** Long enough to type through an operator or a two-digit number, short enough to feel immediate. */
export const SETTLE_MS = 600;

/**
 * True while `value` is still changing, flipping false `delay` ms after the last change.
 *
 * Starts settled: a value loaded from storage was authored in an earlier session, so a defect in it
 * is already true and must be reported on arrival rather than after a timer. Only an EDIT opens the
 * grace period.
 */
export function useIsSettling(value: string | undefined, delay: number = SETTLE_MS): boolean {
  const [previous, setPrevious] = useState(value);
  const [settling, setSettling] = useState(false);

  /**
   * Adjusted DURING render, not in an effect. This is the whole correctness of the hook.
   *
   * Setting `settling` from an effect made it report one render late: on the very render that
   * first sees a changed value it still answered "settled", so every consumer got one frame in
   * which a brand-new fault looked like an old one. `useDelayedStickyError` then latched itself
   * visible on that frame and stayed latched, which is why malformed equations appeared instantly
   * and flickered in and out as the value crossed between valid and invalid while typing —
   * precisely the behaviour the delay exists to prevent.
   *
   * React supports this pattern: a conditional state update during render is discarded-and-rerun
   * before anything is painted, so no stale frame ever reaches the screen.
   */
  const changed = previous !== value;
  if (changed) {
    setPrevious(value);
    setSettling(true);
  }

  useEffect(() => {
    if (!settling) return;
    const id = setTimeout(() => setSettling(false), delay);
    return () => clearTimeout(id);
  }, [settling, value, delay]);

  /**
   * `|| changed`, and it is load-bearing.
   *
   * The state set just above does not apply until React re-runs this component, so on the very
   * pass that first sees a new value `settling` is still the OLD false. Returning it would answer
   * "settled" about a value that just changed — the same one-frame lie moving the update out of an
   * effect was meant to end, only now inside a single render instead of across two.
   *
   * That frame is discarded and never painted, so nothing is visibly wrong. But a CALLER can leave
   * a mark on it: `useDelayedStickyError` writes `shownRef.current = true` when it is told the
   * value has settled, and a ref write survives the discard while the render output does not. The
   * result was a brand-new fault latching visible on the frame it appeared, which is precisely the
   * behaviour the delay exists to prevent.
   *
   * Reporting `changed` alongside the state keeps this pass honest, so no caller can act on the
   * stale answer in the first place.
   */
  return settling || changed;
}

// ── Which issues are transient ───────────────────────────────────────────────

/**
 * Issues that are a statement about a branching equation, or are computed from one.
 *
 * `invalid-equation` is the obvious member: every equation is unparseable at some point on the way
 * to being parseable. The coverage and reachability keys join it at one remove — they are derived
 * FROM the equation, so a gap computed from `{n}*` describes a string nobody meant to author, and
 * it appears and vanishes as the rest is typed.
 *
 * These key names are shared vocabulary: dropdown, fitb, gps, keyword, mcq, ordering and scale all
 * emit exactly this set, which is why one predicate serves all seven.
 */
export function isEquationDerivedIssue(key: string): boolean {
  return key === 'invalid-equation'
    || key.startsWith('coverage-')
    || (key.startsWith('branch-') && key.endsWith('-unreachable'));
}

/**
 * The same, for an ANSWER being typed as an equation rather than the branching selector.
 *
 * Three spellings of one idea, because the blocks name their answers differently: MCQ and Dropdown
 * have `option-*`, Ordering has `item-*`, and Slider and Numerical have `answer-formula-invalid*`.
 * The collision checks belong here too — they compare resolved values, so a half-typed formula
 * makes two answers collide that will not collide a keystroke later.
 */
export function isAnswerEquationIssue(key: string): boolean {
  return key.endsWith('-unevaluable')
    || key.startsWith('answer-formula-invalid')
    || key === 'option-collision' || key === 'option-chip-collision'
    || key === 'item-formula-collision' || key === 'item-chip-collision'
    || key === 'answer-field-invalid'
    // Typed into the same way, and wrong for most of the way in: `((300+1)^2)/log(` is not an
    // equation yet, and saying so on the keystroke that produced it is noise, not help.
    || key === 'math-error-equation';
}

/**
 * The answer positions a Slider or Numerical block grades against, as one settle key.
 *
 * Structurally typed rather than tied to either block's data type: both pick their answers the same
 * way — `answers` under `exact`, `betweenAnswers` under `between`, `threshold` otherwise — and both
 * validation functions (`sliderIssues`, `numericalIssues`) already extract them with these
 * exact three lines. Keyed on the positions alone, not the whole block, so toggling an unrelated
 * setting does not open a grace period on the answers.
 */
export interface AnswerPositionSource {
  conditionType: string;
  /** `mathErrorEquation` is Numerical's only; every other block leaves it undefined. */
  answers: Array<{ value: string; mathErrorEquation?: string }>;
  betweenAnswers: Array<{ low: string; high: string; mathErrorEquationLow?: string; mathErrorEquationHigh?: string }>;
  /** Absent on Compass, whose conditions are exact|between only. */
  threshold?: string;
}

export function answerPositionsKey(d: AnswerPositionSource, handleB?: AnswerPositionSource | null): string {
  // Math Error's override equations are in here too. They are answer equations in every sense that
  // matters to this key — typed character by character, invalid for most of the journey, and
  // attached to the answer they compute — so an issue derived from one has to wait for the typing
  // to stop, exactly as `answer-field-invalid` does. Undefined everywhere but Numerical.
  const pick = (src: AnswerPositionSource): string[] =>
    src.conditionType === 'exact' ? src.answers.flatMap(a => [a.value, a.mathErrorEquation ?? ''])
      : src.conditionType === 'between'
        ? src.betweenAnswers.flatMap(a => [a.low, a.high, a.mathErrorEquationLow ?? '', a.mathErrorEquationHigh ?? ''])
      : [src.threshold ?? ''];
  return [...pick(d), ...(handleB ? pick(handleB) : [])].join('\u0000');
}

/**
 * Drops the transient equation defects from an issue list — first appearance only.
 *
 * `alreadyShown` carries the keys this list was displaying before the current edit, and they are
 * exempt. Without it the delay applied to EVERY keystroke, not just the first, so an error already
 * on screen blinked off and back on as the teacher typed — which reads as a flicker rather than as
 * a considered pause. An issue that has left `list` is simply gone; nothing here can resurrect it,
 * so a fix still clears instantly.
 */
export function settleIssues(
  list: BlockIssue[], equationSettling: boolean, answersSettling: boolean, alreadyShown?: ReadonlySet<string>,
): BlockIssue[] {
  if (!equationSettling && !answersSettling) return list;
  return list.filter(i => {
    if (alreadyShown?.has(i.key)) return true;
    return !(equationSettling && isEquationDerivedIssue(i.key)) &&
           !(answersSettling && isAnswerEquationIssue(i.key));
  });
}

/**
 * The whole gate, for a block that computes its issues twice.
 *
 * Every input block builds TWO issue lists: one from `block.data`, read by the collapsed card and
 * the test panel, and one from live editor state, read by the editor panel. They are frequently
 * rendered by two DIFFERENT instances of the block component — the canvas card has
 * `isEditing === false` and its own copy of the editor state, which never moves while the teacher
 * types into the editor's instance.
 *
 * So each list has to be gated by the settling of the value IT was computed from, and this returns
 * one filter per list rather than one filter. Sharing a single flag between them is the bug this
 * shape exists to prevent: it leaves the test panel asserting an error the editor denies.
 *
 * Pass `undefined` for anything a block does not have — Scale has no answer equations, Slider has
 * no branching equation, and an undefined value simply never settles.
 */
export function useIssueSettling(
  live: { equation?: string; answers?: string },
  stored: { equation?: string; answers?: string },
): {
  settleLive: (l: BlockIssue[]) => BlockIssue[];
  settleStored: (l: BlockIssue[]) => BlockIssue[];
} {
  const liveEquation = useIsSettling(live.equation);
  const liveAnswers = useIsSettling(live.answers);
  const storedEquation = useIsSettling(stored.equation);
  const storedAnswers = useIsSettling(stored.answers);
  // What each list displayed last render, so the delay applies to a NEW error only. Committed in
  // an effect rather than during render, so the value read here is always the previous render's.
  const shownLive = useRef<ReadonlySet<string>>(new Set());
  const shownStored = useRef<ReadonlySet<string>>(new Set());
  const pendingLive = useRef<ReadonlySet<string>>(new Set());
  const pendingStored = useRef<ReadonlySet<string>>(new Set());
  useEffect(() => {
    shownLive.current = pendingLive.current;
    shownStored.current = pendingStored.current;
  });
  return {
    settleLive: l => {
      const out = settleIssues(l, liveEquation, liveAnswers, shownLive.current);
      pendingLive.current = new Set(out.map(i => i.key));
      return out;
    },
    settleStored: l => {
      const out = settleIssues(l, storedEquation, storedAnswers, shownStored.current);
      pendingStored.current = new Set(out.map(i => i.key));
      return out;
    },
  };
}

// ── The one visibility rule ──────────────────────────────────────────────────

/**
 * "Reward early, punish late", the standard asymmetry for field validation, in one hook.
 *
 *   not yet shown  ->  stay quiet until `watch` stops changing
 *   already shown  ->  stay lit continuously, and clear the instant `active` goes false
 *
 * Slow to accuse, quick to forgive. The delay applies to the FIRST appearance only; once an error
 * is on screen it does not blink off on the next keystroke, because it is the thing the teacher is
 * reading in order to fix the value. And a fix clears it immediately — no timer stands between
 * correcting a value and being told it is correct.
 *
 * This replaces clearing-on-focus, which was the previous answer and is backwards: it hid the
 * marker at exactly the moment the teacher clicked in to act on it, and for a collision it hid
 * half the evidence (the other colliding row). Focus is a poor proxy for "still working on it";
 * "has the value stopped changing" is the real question, and this asks it directly.
 */
export function useDelayedStickyError(active: boolean, watch: string | undefined, delay: number = SETTLE_MS): boolean {
  const settling = useIsSettling(watch, delay);
  const shownRef = useRef(false);
  // Cleared the moment the fault goes away, so `active` alone decides when to stop.
  if (!active) shownRef.current = false;
  else if (!settling) shownRef.current = true;
  return active && shownRef.current;
}
